<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import {
    fetchDiagConfig, fetchDiagCerts, fetchSendQueueState, unattributedInvoicesApi,
    adminInvoicesApi,
  } from '../stores/api.js';
  import { isAdmin } from '../stores/roles.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { environmentLabel } from '../lib/labels.js';
  import { CheckCircle, XCircle, Shield, Settings, Trash2, AlertTriangle, Loader2, Send, Inbox } from '../lib/icons/index.js';

  let configDiag = null;
  let certsDiag = null;
  let sendQueue = null;
  let unattributed = null;
  let loading = true;
  let error = null;

  // Re-attribution of received invoices with no company
  let reattributing = false;
  let reattributeError = '';
  let reattributeResult = null;

  // Wipe-all-invoices state
  let showWipeModal = false;
  let wipePhrase = '';
  let wiping = false;
  let wipeError = '';
  let wipeResult = null;

  onMount(async () => {
    try {
      const [cfg, certs, queue, orphans] = await Promise.all([
        fetchDiagConfig().catch(e => ({ error: e.message })),
        fetchDiagCerts().catch(e => ({ error: e.message })),
        fetchSendQueueState().catch(e => ({ error: e.message })),
        unattributedInvoicesApi.list().catch(e => ({ error: e.message })),
      ]);
      configDiag = cfg;
      certsDiag = certs;
      sendQueue = queue;
      unattributed = orphans;
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  });

  async function reattribute() {
    reattributeError = '';
    reattributeResult = null;
    reattributing = true;
    try {
      reattributeResult = await unattributedInvoicesApi.reattribute();
      unattributed = await unattributedInvoicesApi.list();
    } catch (err) {
      reattributeError = err.message;
    } finally {
      reattributing = false;
    }
  }

  /** Human duration for how long the send queue has been held. */
  function pausedFor(ms) {
    if (ms == null) return '';
    const minutes = Math.max(1, Math.round(ms / 60000));
    if (minutes < 60) return tImmediate('diagnostics.sendQueue.minutes', { n: minutes });
    return tImmediate('diagnostics.sendQueue.hours', { n: Math.floor(minutes / 60) });
  }

  function certStatusClass(cert) {
    if (cert.status === 'error') return 'border-red-500';
    if (cert.daysUntilExpiry < 30) return 'border-yellow-500';
    return 'border-green-500';
  }

  // Aggregate plain-Italian verdict over certs + config path checks, so a
  // non-technical user gets "all good / a tech is needed" without reading certs.
  $: certList = Array.isArray(certsDiag) ? certsDiag : [];
  $: hasDiagData = certList.length > 0 || !!(certsDiag && certsDiag.error) || !!configDiag;
  $: pathProblems = configDiag?.pathChecks ? configDiag.pathChecks.filter((c) => !c.exists).length : 0;
  $: certProblem = !!(certsDiag && certsDiag.error) || certList.some((c) => c.status !== 'ok');
  $: certExpiringSoon = certList.some((c) => c.status === 'ok' && c.daysUntilExpiry < 30);
  $: verdict = (certProblem || pathProblems > 0) ? 'error' : (certExpiringSoon ? 'warn' : 'ok');
  $: verdictClass = verdict === 'ok' ? 'border-green-500' : verdict === 'warn' ? 'border-yellow-500' : 'border-red-500';

  function openWipe() {
    wipePhrase = '';
    wipeError = '';
    wipeResult = null;
    showWipeModal = true;
  }
  function closeWipe() {
    if (wiping) return;
    showWipeModal = false;
  }
  async function confirmWipe() {
    wipeError = '';
    wipeResult = null;
    if (wipePhrase !== tImmediate('diagnosticsAdmin.wipeAllConfirmPhrase')) {
      wipeError = tImmediate('diagnosticsAdmin.wipeAllConfirmBody');
      return;
    }
    wiping = true;
    try {
      const r = await adminInvoicesApi.wipeAll();
      wipeResult = r;
    } catch (err) {
      wipeError = err.message;
    } finally {
      wiping = false;
    }
  }
</script>

<Layout>
  <div class="page-header">
    <h1 class="section-title">{$t('diagnostics.title')}</h1>
    <p class="text-muted text-sm">{$t('diagnostics.subtitle')}</p>
  </div>

  {#if loading}
    <PageLoader />
  {:else if error}
    <ErrorBanner {error} />
  {:else}
    <div class="space-y-6">
      <!-- Plain-Italian aggregate verdict -->
      {#if hasDiagData}
        <div class="card p-4 border-w {verdictClass}" role="status" aria-live="polite">
          <div class="flex items-start gap-3">
            {#if verdict === 'ok'}
              <CheckCircle size={22} class="text-success flex-shrink-0 mt-0.5" />
            {:else if verdict === 'warn'}
              <AlertTriangle size={22} class="text-warning flex-shrink-0 mt-0.5" />
            {:else}
              <XCircle size={22} class="text-danger flex-shrink-0 mt-0.5" />
            {/if}
            <div>
              <p class="font-bold text-heading">
                {verdict === 'ok' ? $t('diagnostics.verdict.okTitle') : verdict === 'warn' ? $t('diagnostics.verdict.warnTitle') : $t('diagnostics.verdict.errorTitle')}
              </p>
              <p class="text-sm text-muted mt-1">
                {verdict === 'ok' ? $t('diagnostics.verdict.okBody') : verdict === 'warn' ? $t('diagnostics.verdict.warnBody') : $t('diagnostics.verdict.errorBody')}
              </p>
            </div>
          </div>
        </div>
      {/if}

      <!-- Config -->
      <div class="card p-5">
        <div class="flex items-center gap-2 mb-4">
          <Settings size={18} class="text-action" />
          <h2 class="text-lg text-heading font-display">{$t('diagnostics.configuration')}</h2>
        </div>

        {#if configDiag?.error}
          <ErrorBanner error={configDiag.error} />
        {:else}
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div class="p-3 border-w" style="border-color: var(--color-border-default); background-color: var(--color-surface-light);">
              <span class="eyebrow text-muted inline-flex items-center gap-1">{$t('diagnostics.environment')}<InfoHint term="ambiente" /></span>
              <!-- "test" / "production" is the server's own enum; the reader
                   needs to know whether invoices go out for real. -->
              <p class="text-heading font-bold mt-1">{configDiag?.environment ? environmentLabel(configDiag.environment) : '-'}</p>
            </div>
            {#if $isAdmin}
              <div class="p-3 border-w" style="border-color: var(--color-border-default); background-color: var(--color-surface-light);">
                <span class="eyebrow text-muted">{$t('diagnostics.nodeEnv')}</span>
                <p class="text-heading font-bold mt-1">{configDiag?.nodeEnv || '-'}</p>
              </div>
            {/if}
          </div>

          {#if configDiag?.pathChecks && $isAdmin}
            <details class="mt-2">
              <summary class="eyebrow eyebrow-lg text-muted mb-3 cursor-pointer">{$t('diagnostics.filePaths')}</summary>
              <div class="space-y-2 mt-2">
                {#each configDiag.pathChecks as check}
                  <div class="flex items-center gap-2 text-sm">
                    {#if check.exists}
                      <CheckCircle size={16} class="text-success flex-shrink-0" />
                    {:else}
                      <XCircle size={16} class="text-danger flex-shrink-0" />
                    {/if}
                    <span class="font-bold text-heading">{check.field}</span>
                    <span class="text-muted text-xs break-all">{check.path}</span>
                  </div>
                {/each}
              </div>
            </details>
          {/if}
        {/if}
      </div>

      <!-- Send queue: why nothing is going out -->
      {#if sendQueue && !sendQueue.error}
        <div class="card p-5">
          <div class="flex items-center gap-2 mb-2">
            <Send size={18} class="text-action" />
            <h2 class="text-lg text-heading font-display">{$t('diagnostics.sendQueue.title')}</h2>
          </div>
          <p class="text-muted text-xs mb-4">{$t('diagnostics.sendQueue.help')}</p>

          {#if !sendQueue.running}
            <div class="p-3 border-w border-yellow-500/50" style="background-color: var(--color-surface-light);">
              <p class="text-sm text-heading font-bold">{$t('diagnostics.sendQueue.notRunningTitle')}</p>
              <p class="text-xs text-muted mt-1">{$t('diagnostics.sendQueue.notRunningBody')}</p>
            </div>
          {:else if !sendQueue.paused}
            <div class="flex items-start gap-3 p-3 border-w border-green-500/50" style="background-color: var(--color-surface-light);">
              <CheckCircle size={18} class="text-success flex-shrink-0 mt-0.5" />
              <div>
                <p class="text-sm text-heading font-bold">{$t('diagnostics.sendQueue.activeTitle')}</p>
                <p class="text-xs text-muted mt-1">{$t('diagnostics.sendQueue.activeBody')}</p>
              </div>
            </div>
          {:else}
            <div class="space-y-3">
              {#each sendQueue.dependencies.filter((d) => d.paused) as dep}
                <div class="flex items-start gap-3 p-3 border-w border-red-500/50" style="background-color: var(--color-surface-light);">
                  <AlertTriangle size={18} class="text-danger flex-shrink-0 mt-0.5" />
                  <div>
                    <p class="text-sm text-heading font-bold">
                      {dep.dependency === 'signer'
                        ? $t('diagnostics.sendQueue.pausedSignerTitle')
                        : $t('diagnostics.sendQueue.pausedSdiTitle')}
                    </p>
                    <p class="text-xs text-muted mt-1">
                      {dep.dependency === 'signer'
                        ? $t('diagnostics.sendQueue.pausedSignerBody')
                        : $t('diagnostics.sendQueue.pausedSdiBody')}
                    </p>
                    {#if dep.pausedForMs != null}
                      <p class="text-xs text-muted mt-1">
                        {$t('diagnostics.sendQueue.pausedFor')}: <span class="text-heading font-bold">{pausedFor(dep.pausedForMs)}</span>
                      </p>
                    {/if}
                    {#if dep.reason && $isAdmin}
                      <p class="text-xs font-mono text-muted mt-1 break-all">{dep.reason}</p>
                    {/if}
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/if}

      <!-- Received invoices nobody owns -->
      {#if unattributed && !unattributed.error && unattributed.count > 0}
        <div class="card p-5 border-w border-yellow-500/50">
          <div class="flex items-center gap-2 mb-2">
            <Inbox size={18} class="text-warning" />
            <h2 class="text-lg text-heading font-display">{$t('diagnostics.unattributed.title')}</h2>
          </div>
          <p class="text-muted text-xs mb-4">{$t('diagnostics.unattributed.help')}</p>

          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead>
                <tr class="text-left text-muted uppercase tracking-wider">
                  <th class="py-1 pr-3">{$t('diagnostics.unattributed.number')}</th>
                  <th class="py-1 pr-3">{$t('diagnostics.unattributed.supplier')}</th>
                  <th class="py-1 pr-3">{$t('diagnostics.unattributed.addressee')}</th>
                  <th class="py-1">{$t('diagnostics.unattributed.partitaIva')}</th>
                </tr>
              </thead>
              <tbody>
                {#each unattributed.invoices as inv}
                  <tr class="border-t" style="border-color: var(--color-border-default);">
                    <td class="py-1 pr-3 text-body">{inv.invoiceNumber || inv.identificativoSdi || '-'}</td>
                    <td class="py-1 pr-3 text-body">{inv.fornitore || inv.senderTaxId || '-'}</td>
                    <td class="py-1 pr-3 text-body">{inv.destinatario || '-'}</td>
                    <td class="py-1 font-mono text-heading font-bold">{inv.destinatarioIdCodice || inv.destinatarioCodiceFiscale || inv.receiverTaxId || '-'}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>

          <p class="text-muted text-xs mt-4">{$t('diagnostics.unattributed.whatToDo')}</p>

          {#if reattributeResult}
            <p class="text-xs mt-3" style="color: var(--color-success);">{reattributeResult.message}</p>
          {/if}
          {#if reattributeError}
            <div class="mt-3 p-2 bg-red-500/10 border border-red-500/30 text-danger text-xs">{reattributeError}</div>
          {/if}

          <button on:click={reattribute} disabled={reattributing}
            class="btn btn-primary btn-sm gap-2 mt-4">
            {#if reattributing}<Loader2 size={14} class="animate-spin" />{/if}
            {$t('diagnostics.unattributed.retryCta')}
          </button>
        </div>
      {/if}

      <!-- Certs -->
      <div class="card p-5">
        <div class="flex items-center gap-2 mb-4">
          <Shield size={18} class="text-accent" />
          <h2 class="text-lg text-heading font-display">{$t('diagnostics.certificates')}</h2>
        </div>

        <p class="text-muted text-xs mb-4">{$t('diagnostics.certsHelp')}</p>

        {#if certsDiag?.error}
          <ErrorBanner error={certsDiag.error} />
        {:else if Array.isArray(certsDiag)}
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {#each certsDiag as cert}
              <div class="p-4 border-w {certStatusClass(cert)}" style="background-color: var(--color-surface-light);">
                <div class="flex items-center justify-between mb-2">
                  <span class="font-bold text-heading text-sm">{cert.label}</span>
                  {#if cert.status === 'ok'}
                    <CheckCircle size={16} class="text-success" />
                  {:else}
                    <XCircle size={16} class="text-danger" />
                  {/if}
                </div>
                {#if cert.status === 'ok'}
                  <p class="text-xs mb-2">
                    <span class="text-muted">{$t('diagnostics.cert.expiresIn')}:</span>
                    <span class="{cert.daysUntilExpiry < 30 ? 'text-warning' : 'text-success'} font-bold">
                      {cert.daysUntilExpiry} {$t('diagnostics.cert.days')}
                    </span>
                  </p>
                  <details>
                    <summary class="eyebrow text-muted cursor-pointer">{$t('diagnostics.technicalDetails')}</summary>
                    <dl class="space-y-1 text-xs mt-2">
                      <div><dt class="inline text-muted">{$t('diagnostics.cert.subject')}:</dt> <dd class="inline text-body">{cert.subject}</dd></div>
                      <div><dt class="inline text-muted">{$t('diagnostics.cert.issuer')}:</dt> <dd class="inline text-body">{cert.issuer}</dd></div>
                      <div><dt class="inline text-muted">{$t('diagnostics.cert.valid')}:</dt> <dd class="inline text-body">{cert.validFrom} &mdash; {cert.validTo}</dd></div>
                      {#if cert.fingerprint256}
                        <div><dt class="inline text-muted">{$t('diagnostics.cert.sha256')}:</dt> <dd class="inline text-body break-all">{cert.fingerprint256.slice(0, 40)}...</dd></div>
                      {/if}
                    </dl>
                  </details>
                {:else}
                  <p class="text-danger text-xs">{cert.error || $t('diagnostics.cert.failedToLoad')}</p>
                {/if}
              </div>
            {/each}
          </div>
        {:else}
          <p class="text-muted">{$t('diagnostics.noCerts')}</p>
        {/if}
      </div>
      {#if $isAdmin}
        <!-- Danger zone -->
        <div class="card p-5 border-w border-red-500/40">
          <div class="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} class="text-danger" />
            <h2 class="text-lg text-heading font-display">{$t('diagnosticsAdmin.danger')}</h2>
          </div>
          <h3 class="text-sm font-bold mb-1" style="color: var(--color-text-heading);">{$t('diagnosticsAdmin.wipeAllTitle')}</h3>
          <p class="text-xs font-mono mb-4" style="color: var(--color-text-muted);">{$t('diagnosticsAdmin.wipeAllBody')}</p>
          <button on:click={openWipe}
            class="btn btn-danger btn-sm gap-2">
            <Trash2 size={14} />
            {$t('diagnosticsAdmin.wipeAllButton')}
          </button>
        </div>
      {/if}
    </div>
  {/if}

  {#if showWipeModal}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 fade-in" on:click={closeWipe}>
      <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
      <div on:click|stopPropagation class="card p-6 max-w-lg w-full mx-4 border-w border-red-500/50">
        <div class="flex items-center gap-2 mb-3">
          <AlertTriangle size={20} class="text-danger" />
          <h3 class="text-lg font-bold" style="color: var(--color-text-heading);">{$t('diagnosticsAdmin.wipeAllConfirmTitle')}</h3>
        </div>

        {#if wipeResult}
          <p class="text-sm font-mono mb-3" style="color: var(--color-success);">{$t('diagnosticsAdmin.wipeAllSuccess')}</p>
          <pre class="text-xs font-mono p-3 border-w overflow-x-auto" style="border-color: var(--color-tint-medium); color: var(--color-text-body);">{JSON.stringify(wipeResult.deleted, null, 2)}</pre>
          <div class="flex justify-end mt-4">
            <button on:click={closeWipe} class="btn btn-primary btn-sm">
              {$t('common.close')}
            </button>
          </div>
        {:else}
          <p class="text-sm font-mono mb-3" style="color: var(--color-text-body);">{$t('diagnosticsAdmin.wipeAllConfirmBody')}</p>
          <p class="text-sm font-mono font-bold mb-3 text-danger">{$t('diagnosticsAdmin.wipeAllConfirmPhrase')}</p>
          <input
            bind:value={wipePhrase}
            placeholder={$t('diagnosticsAdmin.wipeAllConfirmPhrase')}
            disabled={wiping}
            class="input mb-3"
            style="border-color: color-mix(in srgb, var(--color-danger) 40%, transparent); color: var(--color-text-body);"
          />
          {#if wipeError}
            <div class="mb-3 p-2 bg-red-500/10 border border-red-500/30 text-danger text-xs font-mono">{wipeError}</div>
          {/if}
          <div class="flex justify-end gap-2">
            <button on:click={closeWipe} disabled={wiping}
              class="btn btn-secondary btn-sm">
              {$t('common.cancel')}
            </button>
            <button on:click={confirmWipe} disabled={wiping || wipePhrase !== $t('diagnosticsAdmin.wipeAllConfirmPhrase')}
              class="btn btn-danger btn-sm gap-2">
              {#if wiping}<Loader2 size={14} class="animate-spin" />{:else}<Trash2 size={14} />{/if}
              {$t('diagnosticsAdmin.wipeAllConfirmCta')}
            </button>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</Layout>
